import React, { useMemo } from 'react';
import { Typography, Stack } from '@mui/material';
import { styled } from '@mui/system';
import { CareProgramCard } from 'components/CareProgramCard/CareProgramCard';
import type { EnrollmentStatus, Program, ProgramSection } from 'features/resources/types';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';

export interface CareProgramsListProps {
  sections: ProgramSection[];
  onProgramClick?: (program: Program) => void;
  selectedProgramId?: number;
  resultsText?: string;
}

const Container = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  background: theme.palette.brandBackgroundPrimary.main,
}));

const SectionHeader = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
}));

export const CareProgramsList: React.FC<CareProgramsListProps> = ({
  sections,
  onProgramClick,
  selectedProgramId,
  resultsText,
}) => {
  const { translation } = useTranslationContext();
  const statusLabelMap = useMemo<Record<EnrollmentStatus, string | undefined>>(
    () => ({
      not_enrolled: undefined,
      requested: translation('requested'),
      enrolled: translation('enrolled'),
      declined: undefined,
      completed: undefined,
    }),
    [translation],
  );

  return (
    <Container>
      {resultsText && (
        <Typography variant="callout" color="grey.700">
          {resultsText}
        </Typography>
      )}
      {sections.map((section) => (
        <React.Fragment key={section.programType}>
          <SectionHeader spacing={0}>
            <Typography variant="headline" color="grey.800">
              {section.title}
            </Typography>
            <Typography variant="subheadline" color="grey.600">
              {section.description}
            </Typography>
          </SectionHeader>
          <Stack spacing={1}>
            {section.programs.map((program) => (
              <CareProgramCard
                statusLabel={statusLabelMap[program.enrollmentStatus]}
                key={program.id}
                title={program.title}
                programType={program.programType}
                selected={program.id === selectedProgramId}
                onClick={() => onProgramClick?.(program)}
              />
            ))}
          </Stack>
        </React.Fragment>
      ))}
    </Container>
  );
};
